import { useMemo } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import createDOMPurify from "dompurify";
import { Link } from "react-router-dom";

import {
  FileText,
  ChartNoAxesColumnIncreasing,
  BookOpen,
  MessageCircle,
  MessageSquare,
} from "lucide-react";

import {
  DataBlockReferencesProps,
  DataBlockReferencesTypes,
} from "@/features/datablocks/details/types";
import { DataBlockReference } from "@/redux/datablocks/types";

import "../styles.css";

const DOMPurify = createDOMPurify(window);

const getReferenceTypes = (
  t: (key: string) => string,
): DataBlockReferencesTypes => ({
  surveys: {
    title: t("datablocks.labels.surveys"),
    icon: FileText,
    getLink: (value: DataBlockReference) => `/reports/${value.id}`,
  },
  surveyViews: {
    title: t("datablocks.labels.surveyViews"),
    icon: ChartNoAxesColumnIncreasing,
    getLink: (value: DataBlockReference) => `/reports/views/${value.id}`,
  },
  chapters: {
    title: t("datablocks.labels.chapters"),
    icon: BookOpen,
    getLink: (value: DataBlockReference) => `/evidence/${value.id}`,
  },
  discussions: {
    title: t("datablocks.labels.discussions"),
    icon: MessageCircle,
    getLink: (value: DataBlockReference) => `/discussons/${value.id}`,
  },
  comments: {
    title: t("datablocks.labels.comments"),
    icon: MessageSquare,
    getLink: (value: DataBlockReference) => `/discussons/${value.discussionId}`,
  },
});

const References = ({ references }: DataBlockReferencesProps) => {
  const { t } = useTranslation();
  const referencesTypes = useMemo(() => getReferenceTypes(t), [t]);

  const hasReferences = useMemo(() => {
    if (!references) return false;
    return Object.values(references).some((entry) => entry.length > 0);
  }, [references]);

  if (!hasReferences) {
    return (
      <div className="data-block-no-references-label body-s-regular">
        {t("datablocks.labels.noReferencesExist")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-300">
      {Object.entries(references).map(([type, details]) => {
        if (!details?.length) return null;

        const config = referencesTypes[type as keyof DataBlockReferencesTypes];
        const Icon = config.icon as React.ComponentType<{ size?: number }>;

        return (
          <div key={type}>
            <div className="flex gap-200 items-center">
              <Icon size={16} />

              <h6 className="body-s-medium">{config.title}</h6>
            </div>

            <ul className="data-block-reference-list">
              {details.map((value: DataBlockReference) => (
                <li key={value.label}>
                  <Link
                    className="data-block-reference-list-item"
                    to={config.getLink(value)}
                  >
                    {type === "comments" ? (
                      <div
                        style={{ wordWrap: "break-word" }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            _.truncate(value.label, { length: 80 }),
                            { USE_PROFILES: { html: true } },
                          ),
                        }}
                      />
                    ) : (
                      value.label
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default References;
